// =============================================================================
// stockOutbox.js — a small DURABLE outbox for stock tap writes (decommission
// step 2: make the Supabase stock write durable BEFORE the Google Sheet fallback
// is ever removed). A near-verbatim sibling of selloutOutbox.js (proven, tested by
// tools/lane_n_outbox_test.mjs) with its OWN localStorage key so the two queues
// never mix.
//
// WHY. Today every stock tap shadows to Supabase through the best-effort shadow()
// in stockSync.js: a single un-awaited insert whose failure is swallowed as a
// console.warn. That is safe WHILE the Sheet is the source of truth, but the
// decommission makes Supabase authoritative — and then a swallowed write is a lost
// request = a stockout. This buffer makes the write survive a network blip, an
// unmount/navigate, or a tablet sleep: a tap ENQUEUES its intended row SYNCHRONOUSLY
// (durable before any await), the write is attempted immediately, and a self-arming
// flusher drains anything left when connectivity/visibility returns.
//
// IDEMPOTENCY. Every entry carries an idem_key that is BOTH the queue's key and the
// server's dedup handle:
//   * kitchen events (running_low/out_of_stock/back_in_stock/ordered/delivered/
//     wastage) reuse the existing idem(site,action,item,day) — a unique index on
//     stock_activity.idem_key collapses a double-tap/retry to one row.
//   * stock_requests lifecycle ops (request/undo/received) have NO server idem_key
//     (dedup is the stock_requests_one_open partial index, deliberately — see
//     stock.createStockRequest). Their queue key carries a per-tap nonce so a
//     legitimate re-request after a close is NEVER collapsed away in the queue; the
//     server one-open index still prevents two simultaneous opens and the insert
//     treats that collision (23505) as landed.
//
// Pure of any Supabase import: the actual insert + the activity line are INJECTED
// (the `insert` / `onLanded` seams), so this file is unit-testable with no network.
// It NEVER throws.
// =============================================================================

export const OUTBOX_KEY = "rolled.stock.outbox.v1";
const MAX_ENTRIES = 200; // a hard cap; a genuinely stuck kiosk drops the OLDEST, logged.

// Storage seam — real localStorage in the app, an injectable Map-like in tests.
function defaultStore() {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch (e) { /* SSR / privacy mode */ }
  // In-memory fallback so nothing throws where localStorage is absent.
  const m = new Map();
  return { getItem: (k) => (m.has(k) ? m.get(k) : null), setItem: (k, v) => m.set(k, v), removeItem: (k) => m.delete(k) };
}
let _store = defaultStore();
export function _setStore(s) { _store = s; } // test seam

function read() {
  try {
    const raw = _store.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; } // corrupt buffer never breaks a tap
}
function write(list) {
  try { _store.setItem(OUTBOX_KEY, JSON.stringify(list)); return true; }
  catch (e) { console.warn("[stock-outbox] persist failed:", e?.message || e); return false; }
}

// Enqueue (or replace) an entry by idem_key. Synchronous, swallows its own errors,
// returns true if it persisted. `entry` must carry `idem_key`.
export function enqueue(entry) {
  try {
    if (!entry || !entry.idem_key) return false;
    const list = read();
    const i = list.findIndex((e) => e.idem_key === entry.idem_key);
    if (i >= 0) list[i] = { ...list[i], ...entry }; // a double-tap re-enqueues the SAME key -> one entry
    else list.push(entry);
    // Cap: drop the oldest if we somehow run away (a kiosk offline for hours).
    while (list.length > MAX_ENTRIES) {
      const dropped = list.shift();
      console.warn("[stock-outbox] capacity reached — dropping oldest queued write:", dropped?.idem_key);
    }
    return write(list);
  } catch (e) { console.warn("[stock-outbox] enqueue threw:", e?.message || e); return false; }
}

export function remove(idemKey) {
  try {
    const list = read().filter((e) => e.idem_key !== idemKey);
    return write(list);
  } catch (e) { return false; }
}

// Stamp the resolved staff_id onto a queued entry (captured after the per-action
// login) so a flushed-later row lands attributed to the RIGHT person.
export function stampStaff(idemKey, staffId) {
  try {
    if (!staffId) return false;
    const list = read();
    const e = list.find((x) => x.idem_key === idemKey);
    if (!e) return false;
    e.staff_id = staffId;
    return write(list);
  } catch (e) { return false; }
}

export function list() { return read(); }
export function size() { return read().length; }

// --- per-entry landing waiters (the AUTHORITATIVE write path) -----------------
// The best-effort recorders only ever need the aggregate {landed, kept} flush()
// returns. The AUTHORITATIVE write (stockWriteAuthoritative in stockSync.js) must
// know THIS entry's OWN outcome: a flush that lands a DIFFERENT queued row must
// never read as "mine landed" (the kill-criteria — never a green tick over a
// dropped write). `awaitEntry(idem_key)` is registered BEFORE the flush and is
// settled with the entry's OWN insert result the instant flush() processes that
// exact idem_key. A FAILED insert settles the waiter too (honest !ok now — the
// entry STAYS queued for the self-arming flusher, so nothing is lost). A never-
// returning sink can't hang the till: the timeout settles !ok. Purely additive —
// the existing flush() return shape and the recorders are untouched.
// idem_key -> ARRAY of settlers. A LIST, not one settler, because a kitchen
// event's idem_key is DETERMINISTIC (no per-tap nonce), so two concurrent
// authoritative writes for the same action+item+day both wait on the SAME key;
// a single-settler map would orphan the first (it would only resolve on its
// timeout — an ~8s hang + a false "didn't save" over a row that DID land). Every
// settler for the key is resolved together when flush processes that one entry.
const _waiters = new Map(); // idem_key -> [settle, ...]

// The default CAP the authoritative write waits for a SPECIFIC entry to land
// before it reports !ok. It is a ceiling, NOT the typical wait: a normal landing
// resolves the instant flush's insert returns (sub-second). Deliberately generous
// — a shorter cap would report a false !ok on a slow-but-successful write on spotty
// Wi-Fi, and a false failure makes staff re-tap, which for a stock_requests op
// mints a SECOND (nonce'd) row. The screens already flip the tile OPTIMISTICALLY
// before awaiting (foh.jsx setStockRequests / boh-kitchen.jsx setLocalMark), so
// this only bounds when the rollback fires, never the tap's responsiveness.
// stockWriteAuthoritative threads a per-call timeoutMs through, so a surface can
// override it here in one place if it ever needs to.
export const DEFAULT_LANDING_TIMEOUT_MS = 8000;

export function awaitEntry(idemKey, timeoutMs = DEFAULT_LANDING_TIMEOUT_MS) {
  return new Promise((resolve) => {
    let done = false;
    let timer = null;
    const settle = (v) => {
      if (done) return;
      done = true;
      const arr = _waiters.get(idemKey);
      if (arr) {
        const i = arr.indexOf(settle);
        if (i >= 0) arr.splice(i, 1);
        if (arr.length === 0) _waiters.delete(idemKey);
      }
      if (timer) { try { clearTimeout(timer); } catch (e) {} }
      resolve(v);
    };
    const existing = _waiters.get(idemKey);
    if (existing) existing.push(settle); else _waiters.set(idemKey, [settle]);
    if (timeoutMs && timeoutMs > 0) {
      // The timeout is the whole point of the waiter, so it must be allowed to fire
      // — do NOT unref it (that let Node exit before a pure-timeout resolved). On a
      // normal landing settle() clears it, so it never lingers past the write.
      try {
        timer = setTimeout(() => settle({ ok: false, errMsg: "timeout" }), timeoutMs);
      } catch (e) { /* no timers (unusual) — the flush settle still resolves it */ }
    }
  });
}

// Settle the waiter (if any) for one entry with its OWN insert result. Called by
// flush() for every entry it processes, success OR failure.
function settleWaiter(idemKey, res) {
  const arr = _waiters.get(idemKey);
  if (!arr || !arr.length) return;
  const ok = !!(res && (res.ok || res.duplicate));
  let errMsg = "";
  if (!ok) {
    const e = res && res.error;
    errMsg = (e && (e.message || e.code)) || (res && res.errMsg) || "not landed";
  }
  const payload = { ok, duplicate: !!(res && res.duplicate), errMsg };
  // Snapshot: each settle() mutates the array as it removes itself.
  for (const settle of arr.slice()) settle(payload);
}

// Test-only: how many waiters are outstanding (proves none leak after a flush).
export function _waiterCount() { let n = 0; for (const arr of _waiters.values()) n += arr.length; return n; }

// Drain the outbox. `insert(entry)` -> Promise<{ok, duplicate?}>; a landed or
// duplicate row is removed, a failed one is kept for the next drain. `onLanded`
// (optional) fires per landed row so the activity line follows the SAME success
// path (fired for immediate AND flushed-later rows). Concurrency-guarded so
// overlapping triggers (online + a fresh tap) don't double-drain; a flush
// requested WHILE one is running is COALESCED into one more pass, so a row
// enqueued mid-drain (rapid taps at close) still lands promptly. Never throws.
let _flushing = false, _rerun = false;
export async function flush({ insert, onLanded } = {}) {
  if (typeof insert !== "function") return { landed: 0, kept: read().length, error: "no insert fn" };
  if (_flushing) { _rerun = true; return { landed: 0, kept: read().length, skipped: "coalesced" }; }
  _flushing = true;
  let landed = 0;
  try {
    do {
      _rerun = false;
      const attempted = new Set(); // don't re-hammer a failing entry within one flush
      for (const snap of read()) {
        if (attempted.has(snap.idem_key)) continue;
        attempted.add(snap.idem_key);
        // Land the STORE copy, not the snapshot copy (Lane BP review, 13 Sep 2026). A
        // burst of taps (the inbound cart, the +Collected pair) enqueues N entries
        // synchronously, they share ONE login, and then each continuation stamps its
        // OWN staff_id — the first one to finish drains at once, so this loop's
        // snapshot held its siblings UN-stamped while the store copy was stamped a
        // tick later. Inserting the stale copy refused it as "unattributed", settled
        // its waiter ok:false (a FALSE "didn't save" — the row landed on the re-pass)
        // and, on the inbound tile, skipped that line's evidence row. The store is
        // the truth: re-read this entry right before landing it (stockOutbox.test 16).
        const entry = read().find((e) => e.idem_key === snap.idem_key) || snap;
        let res;
        try { res = await insert(entry); } catch (e) { res = { ok: false, error: e }; }
        settleWaiter(entry.idem_key, res);   // per-entry landing notify (authoritative write path) — success OR failure
        if (res && (res.ok || res.duplicate)) {
          remove(entry.idem_key);
          landed++;
          if (onLanded) { try { onLanded(entry, res); } catch (e) { /* activity line is best-effort */ } }
        }
        // a failure leaves the entry in place for the next flush trigger
      }
    } while (_rerun); // a concurrent flush() during the drain -> one more pass to catch new arrivals
  } finally { _flushing = false; }
  return { landed, kept: read().length };
}
